import { useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { RegisterFeedback, PageHeader, EmptyState, StatusBadge } from "./_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, ChevronDown, Truck, ShieldCheck, AlertTriangle,
  Camera, PenLine, Trash2, X, Check, Search, Download,
} from "lucide-react";
import { toast } from "sonner";
import { SignaturePad } from "@/components/SignaturePad";
import { AuthImage, PhotoGrid } from "@/components/AuthImage";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { PreStartChecklistEditor } from "@/components/PreStartChecklistEditor";
import { compressImage } from "@/lib/imageUpload";
import { meterTypeFor } from "@/lib/meterType";

// NZ civil plant pre-start checklist – mechanical/body items in CAPS auto-create damage report on fail
const CHECKLIST = [
  { key: "engine_oil", label: "Engine oil" },
  { key: "coolant", label: "Coolant" },
  { key: "hydraulic_oil", label: "Hydraulic oil" },
  { key: "fuel", label: "Fuel" },
  { key: "brakes", label: "Brakes", mech: true },
  { key: "steering", label: "Steering", mech: true },
  { key: "tyres_tracks", label: "Tyres / Tracks", mech: true },
  { key: "lights_beacon", label: "Lights & beacon", mech: true },
  { key: "horn_reverse_alarm", label: "Horn & reverse alarm", mech: true },
  { key: "mirrors", label: "Mirrors" },
  { key: "seatbelt", label: "Seatbelt", mech: true },
  { key: "rops_fops", label: "ROPS / FOPS", mech: true },
  { key: "fire_extinguisher", label: "Fire extinguisher" },
  { key: "first_aid", label: "First aid kit" },
  { key: "greasing", label: "Greasing" },
  { key: "hoses_leaks", label: "Hoses / leaks", mech: true },
  { key: "bucket_pins", label: "Bucket / attachment pins", mech: true },
  { key: "windscreen", label: "Windscreen", mech: true },
  { key: "body_damage", label: "General body damage", mech: true },
];

const blank = (checklistItems) => ({
  vehicle_id: "",
  hours_or_odometer: "",
  meter_broken: false,
  items: (checklistItems || CHECKLIST).map((c) => ({ key: c.key, label: c.label, status: "pass", notes: "", photo_paths: [] })),
  overall_safe: true,
  defects_notes: "",
  signature_path: null,
  machine_photo_path: "",
});

async function uploadDataUrl(dataUrl, category) {
  // base64 -> Blob directly (fetch(dataUrl) is flaky in some iOS webviews)
  const [head, b64] = dataUrl.split(",");
  const mime = (head.match(/data:(.*?);/) || [])[1] || "image/png";
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  const blob = new Blob([bytes], { type: mime });
  const file = new File([blob], `${category}-${Date.now()}.png`, { type: "image/png" });
  const fd = new FormData();
  fd.append("file", file);
  fd.append("category", category);
  const { data } = await api.post("/files/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data.storage_path;
}

export default function PreStarts() {
  const { user } = useAuth();
  const isField = user.role === "field";
  const [items, setItems] = useState([]);
  const [fleet, setFleet] = useState([]);
  const [open, setOpen] = useState(false);
  const [searchParams] = useSearchParams();
  useEffect(() => { if (searchParams.get("new") === "1") setOpen(true); }, [searchParams]);
  const [expanded, setExpanded] = useState({});
  useEffect(() => {
    const id = searchParams.get("record");
    if (id) setExpanded(current => ({ ...current, [id]: true }));
  }, [searchParams]);
  const [lightbox, setLightbox] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [checklist, setChecklist] = useState({ items: CHECKLIST.map((c) => ({ key: c.key, label: c.label, mech: !!c.mech })), custom: false });

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const load = async () => {
    setLoading(true); setLoadError(false);
    try {
    const requestParams = filter === "unsafe" ? { unsafe_only: true } : {};
    const { data } = await api.get("/pre_starts", { params: requestParams });
    const recordId = searchParams.get("record");
    if (recordId && !data.some(row => row.id === recordId)) {
      try { const result = await api.get(`/pre_starts/${encodeURIComponent(recordId)}`); data.unshift(result.data); }
      catch { toast.error("The linked record is unavailable or you do not have access."); }
    }
    setItems(data);
    } catch (error) {
      setLoadError(true);
      console.warn("Pre-start request failed", { status: error.response?.status });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter, searchParams]);
  useEffect(() => { api.get("/fleet").then((r) => setFleet(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/pre_starts/checklist").then((r) => setChecklist(r.data)).catch(() => {}); }, []);

  function toggle(id) { setExpanded((s) => ({ ...s, [id]: !s[id] })); }

  const onDownload = async (id, rego) => {
    try {
      const r = await api.get(`/pre_starts/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `prestart-${rego || id}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch { toast.error("Could not download PDF"); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this pre-start record? Office/admin only.")) return;
    try {
      await api.delete(`/pre_starts/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const ql = q.trim().toLowerCase();
  const shownItems = items.filter((p) => !ql ||
    [p.vehicle_make_model, p.vehicle_rego, p.user_name, p.operator_name, p.job_title, p.status]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(ql)));

  return (
    <div data-testid="prestarts-page" className={`${isField ? "p-4 pb-24" : "p-6 lg:p-10"} max-w-7xl mx-auto`}>
      <PageHeader
        tag="/ daily plant check"
        title="Pre-Starts"
        subtitle={isField
          ? "Run a pre-start on your machine before operating. Fails on body or mechanical items auto-raise a damage report."
          : "Daily plant pre-start checks logged by field crew. Unsafe checks flag the vehicle and create damage reports automatically."}
        action={
          <Button
            onClick={() => setOpen(true)}
            className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold"
            data-testid="new-prestart-button"
          >
            <Plus className="w-4 h-4 mr-2" /> New Pre-Start
          </Button>
        }
      />

      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <div className="relative flex-1 min-w-[190px] max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search pre-start checks" placeholder="Search machine, rego, operator…"
                 className="pl-9 w-full rounded-lg border-zinc-300 dark:border-white/15 bg-white dark:bg-[#101620]"
                 data-testid="prestart-search-input" />
        </div>
        {!isField && (
          <>
          <Button size="sm" variant={filter === "all" ? "default" : "outline"}
            className={`rounded-sm uppercase font-bold ${filter === "all" ? "bg-zinc-900 text-white" : ""}`}
            onClick={() => setFilter("all")} data-testid="prestart-filter-all">All</Button>
          <Button size="sm" variant={filter === "unsafe" ? "default" : "outline"}
            className={`rounded-sm uppercase font-bold ${filter === "unsafe" ? "bg-red-600 text-white hover:bg-red-700" : "border-red-600 text-red-700 hover:bg-red-50"}`}
            onClick={() => setFilter("unsafe")} data-testid="prestart-filter-unsafe">
            <AlertTriangle className="w-3.5 h-3.5 mr-1.5" /> Unsafe only
          </Button>
          {["admin", "office", "super_admin"].includes(user.role) && (
            <div className="ml-auto">
              <PreStartChecklistEditor checklist={checklist} onSaved={setChecklist} />
            </div>
          )}
          </>
        )}
      </div>

      {loading || loadError ? <RegisterFeedback loading={loading} error={loadError} onRetry={load} label="pre-start checks" /> : shownItems.length === 0 ? (
        <EmptyState
          title={items.length === 0 ? "No pre-starts yet" : "No matches"}
          description={items.length === 0
            ? (isField ? "Tap New Pre-Start to log today's machine check." : "Field workers haven't logged any pre-starts yet.")
            : "Try a different search or filter."}
          action={<Button onClick={() => setOpen(true)} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase tracking-wider font-bold" data-testid="empty-new-prestart"><Plus className="w-4 h-4 mr-2" /> New Pre-Start</Button>}
        />
      ) : (
        <div className="space-y-3">
          {shownItems.map((p) => {
            const isOpen = !!expanded[p.id];
            const photoPath = p.machine_photo_path || fleet.find((v) => v.id === p.vehicle_id)?.photo_path;
            return (
              <div key={p.id} className="hard-card" data-testid={`prestart-card-${p.id}`}>
                <button
                  type="button"
                  onClick={() => toggle(p.id)}
                  className="w-full text-left p-5 hover:bg-zinc-50 transition-colors"
                  data-testid={`prestart-toggle-${p.id}`}
                >
                  <div className="flex items-start gap-3">
                    {photoPath ? (
                      <div className="w-16 h-16 shrink-0 overflow-hidden border border-zinc-200 bg-zinc-100" data-testid={`prestart-thumb-${p.id}`}>
                        <AuthImage path={photoPath} alt={p.vehicle_make_model} className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      <div className="w-16 h-16 shrink-0 flex items-center justify-center border border-zinc-200 bg-zinc-50">
                        <Truck className="w-6 h-6 text-zinc-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-display font-bold uppercase">{p.vehicle_rego}</span>
                            <span className="text-xs text-zinc-500">{p.vehicle_make_model}</span>
                            {p.overall_safe ? (
                              <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border border-green-300">
                                <ShieldCheck className="w-3 h-3" /> Safe
                              </span>
                            ) : (
                          <span className="inline-flex items-center gap-1 bg-red-600 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm">
                            <AlertTriangle className="w-3 h-3" /> UNSAFE
                          </span>
                        )}
                        {p.fail_count > 0 && (
                          <span className="inline-flex items-center gap-1 bg-orange-100 text-orange-800 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border border-orange-300">
                            {p.fail_count} fail{p.fail_count === 1 ? "" : "s"}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-zinc-500 font-mono mt-1">
                        {new Date(p.created_at).toLocaleString("en-NZ")} · {p.user_name}
                        {p.meter_broken ? " · meter not working" : p.hours_or_odometer ? ` · ${p.hours_or_odometer} ${p.meter_type === "km" ? "km" : "hrs"}` : ""}
                      </div>
                    </div>
                        <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                      </div>
                    </div>
                  </div>
                </button>
                {isOpen && (
                  <div className="px-5 pb-5 border-t border-zinc-200 pt-4 space-y-4" data-testid={`prestart-detail-${p.id}`}>
                    <PreStartView record={p} onPhotoClick={setLightbox} />
                    <div className="flex justify-between pt-2 border-t border-zinc-100">
                      <Button size="sm" variant="outline"
                        className="rounded-sm uppercase tracking-wider font-bold text-xs"
                        onClick={() => onDownload(p.id, p.vehicle_rego)}
                        data-testid={`prestart-download-${p.id}`}>
                        <Download className="w-3 h-3 mr-1.5" /> Download PDF
                      </Button>
                      {!isField && (
                        <Button size="sm" variant="ghost"
                          className="rounded-sm text-zinc-500 hover:text-red-600 uppercase tracking-wider font-bold text-xs"
                          onClick={() => onDelete(p.id)}
                          data-testid={`prestart-delete-${p.id}`}>
                          <Trash2 className="w-3 h-3 mr-1.5" /> Delete
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        {open && <PreStartForm fleet={fleet} user={user} checklistItems={checklist.items} onDone={() => { setOpen(false); load(); }} />}
      </Dialog>

      <PhotoLightbox path={lightbox} open={!!lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}

function PreStartView({ record, onPhotoClick }) {
  const fails = (record.items || []).filter((i) => i.status === "fail");
  const passes = (record.items || []).filter((i) => i.status === "pass").length;
  const nas = (record.items || []).filter((i) => i.status === "na").length;
  return (
    <div className="space-y-4 text-sm">
      <div className="grid grid-cols-3 gap-2">
        <Stat icon={<Check className="w-3 h-3" />} label="Pass" value={passes} tone="green" />
        <Stat icon={<X className="w-3 h-3" />} label="Fail" value={fails.length} tone="red" />
        <Stat icon={null} label="N/A" value={nas} tone="zinc" />
      </div>
      {fails.length > 0 && (
        <div className="border-l-2 border-red-500 bg-red-50 p-3 space-y-2">
          <div className="label-tag !text-red-700">Failed items</div>
          {fails.map((f, i) => (
            <div key={i} className="text-xs">
              <div className="font-bold">{f.label}</div>
              {f.notes && <div className="text-zinc-700">{f.notes}</div>}
              {f.photo_paths?.length > 0 && (
                <div className="mt-2"><PhotoGrid paths={f.photo_paths} onClick={(p) => onPhotoClick?.(p)} /></div>
              )}
            </div>
          ))}
        </div>
      )}
      {record.defects_notes && (
        <div>
          <div className="label-tag mb-1">Operator notes</div>
          <p className="text-zinc-800 text-xs whitespace-pre-wrap">{record.defects_notes}</p>
        </div>
      )}
      <div>
        <div className="label-tag mb-2">Full checklist</div>
        <div className="grid sm:grid-cols-2 gap-1.5">
          {(record.items || []).map((i, idx) => (
            <div key={idx} className="flex items-center justify-between border border-zinc-200 px-2 py-1.5 bg-white text-xs">
              <span>{i.label}</span>
              <StatusPill status={i.status} />
            </div>
          ))}
        </div>
      </div>
      {record.signature_path && (
        <div>
          <div className="label-tag mb-1">Operator signature</div>
          <div className="border border-zinc-300 bg-white p-2 inline-block">
            <AuthImage path={record.signature_path} alt="Signature" className="h-14 object-contain" />
          </div>
        </div>
      )}
      {record.created_damage_ids?.length > 0 && (
        <div className="text-xs text-orange-700 italic">
          {record.created_damage_ids.length} damage report{record.created_damage_ids.length === 1 ? "" : "s"} auto-created from failures.
        </div>
      )}
    </div>
  );
}

function Stat({ icon, label, value, tone }) {
  const tones = {
    green: "bg-green-100 text-green-800 border-green-300",
    red: "bg-red-100 text-red-800 border-red-300",
    zinc: "bg-zinc-100 text-zinc-700 border-zinc-300",
  };
  return (
    <div className={`border ${tones[tone]} px-2 py-2 text-center`}>
      <div className="text-[10px] font-bold uppercase tracking-wider flex items-center justify-center gap-1">{icon}{label}</div>
      <div className="text-xl font-display font-bold">{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  if (status === "pass") return <span className="text-green-700 text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1"><Check className="w-3 h-3" /> Pass</span>;
  if (status === "fail") return <span className="text-red-700 text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1"><X className="w-3 h-3" /> Fail</span>;
  return <span className="text-zinc-500 text-[10px] font-bold uppercase tracking-wider">N/A</span>;
}

/* ---------------- Form ---------------- */

function PreStartForm({ fleet, user, checklistItems, onDone }) {
  const [form, setForm] = useState(() => blank(checklistItems));
  const [busy, setBusy] = useState(false);
  const [sigOpen, setSigOpen] = useState(false);
  const [photoBusyKey, setPhotoBusyKey] = useState(null);

  // Pre-select vehicle if user is assigned to one
  useEffect(() => {
    if (!form.vehicle_id && fleet.length) {
      const assigned = fleet.find((v) => v.assigned_user_id === user.id);
      if (assigned) setForm((f) => ({ ...f, vehicle_id: assigned.id }));
    }
  }, [fleet, user.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const setItem = (idx, patch) => {
    setForm((f) => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, ...patch } : it) }));
  };

  const onItemPhoto = async (idx, e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    setPhotoBusyKey(idx);
    try {
      const small = await compressImage(file);
      const fd = new FormData();
      fd.append("file", small, small.name);
      fd.append("category", "pre_start");
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setItem(idx, { photo_paths: [...(form.items[idx].photo_paths || []), data.storage_path] });
      toast.success("Photo added");
    } catch {
      toast.error("Upload failed");
    } finally {
      setPhotoBusyKey(null);
    }
  };

  const onSignatureSave = async (dataUrl) => {
    if (!dataUrl) { setSigOpen(false); return; }
    try {
      const path = await uploadDataUrl(dataUrl, "pre_start_signature");
      setForm((f) => ({ ...f, signature_path: path }));
      toast.success("Signature saved");
    } catch {
      toast.error("Signature upload failed");
    } finally {
      setSigOpen(false);
    }
  };

  const onMachinePhoto = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    setPhotoBusyKey("__machine__");
    try {
      const small = await compressImage(file);
      const fd = new FormData();
      fd.append("file", small, small.name);
      fd.append("category", "pre_start");
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, machine_photo_path: data.storage_path }));
      toast.success("Machine photo added");
    } catch {
      toast.error("Upload failed");
    } finally {
      setPhotoBusyKey(null);
    }
  };

  const failsCount = form.items.filter((i) => i.status === "fail").length;
  const selMeter = meterTypeFor(fleet.find((v) => v.id === form.vehicle_id)?.vehicle_type);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_id) { toast.error("Select a machine"); return; }
    if (selMeter !== "none" && !form.meter_broken && !(Number(form.hours_or_odometer) > 0)) {
      toast.error(selMeter === "km"
        ? "Enter the odometer reading — or tap \"Odometer not working\""
        : "Enter the machine hours — or tap \"Hour clock not working\"");
      return;
    }
    if (!form.signature_path) {
      toast.error("Operator signature is required — tap Sign now at the bottom");
      document.getElementById("prestart-signature-block")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (!form.overall_safe && !form.defects_notes.trim()) {
      toast.error("Add notes describing why this machine is unsafe to operate");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        vehicle_id: form.vehicle_id,
        hours_or_odometer: form.meter_broken ? null : (form.hours_or_odometer ? Number(form.hours_or_odometer) : null),
        meter_broken: !!form.meter_broken,
        items: form.items,
        overall_safe: form.overall_safe,
        defects_notes: form.defects_notes,
        signature_path: form.signature_path,
        machine_photo_path: form.machine_photo_path || null,
      };
      const { data } = await api.post("/pre_starts", payload);
      if (data.created_damage_ids?.length) {
        toast.success(`Pre-start logged · ${data.created_damage_ids.length} damage report(s) auto-created`);
      } else {
        toast.success("Pre-start logged");
      }
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="rounded-sm max-w-2xl max-h-[92vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-display uppercase tracking-tight text-2xl">New machine pre-start</DialogTitle>
        <DialogDescription>
          Tap Pass / Fail / N-A on every item. Any fail requires notes and a photo is encouraged.
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={submit} className="space-y-4">
        <div className={selMeter !== "none" ? "grid grid-cols-[2fr_1fr] gap-3" : "grid grid-cols-1 gap-3"}>
          <div>
            <Label className="label-tag">Machine</Label>
            <Select value={form.vehicle_id} onValueChange={(vid) => setForm((f) => {
              const veh = fleet.find((x) => x.id === vid);
              const mt = meterTypeFor(veh?.vehicle_type);
              const meter = mt === "hours" ? veh?.machine_hours : mt === "km" ? veh?.odometer : null;
              return { ...f, vehicle_id: vid, hours_or_odometer: meter ? String(meter) : "", meter_broken: false };
            })}>
              <SelectTrigger className="mt-2 rounded-sm border-zinc-400" data-testid="prestart-vehicle-select">
                <SelectValue placeholder="Select a vehicle" />
              </SelectTrigger>
              <SelectContent>
                {fleet.length === 0 && <SelectItem value="__none" disabled>No vehicles available</SelectItem>}
                {fleet.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.rego} – {v.make} {v.model}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {selMeter !== "none" && (
          <div>
            <Label className="label-tag">{!form.vehicle_id ? "Meter reading" : selMeter === "km" ? "Odometer (km)" : "Machine hours"}</Label>
            <Input type="number" value={form.meter_broken ? "" : form.hours_or_odometer}
              onChange={(e) => setForm((f) => ({ ...f, hours_or_odometer: e.target.value }))}
              required={!!form.vehicle_id && !form.meter_broken}
              disabled={form.meter_broken}
              className={`mt-2 rounded-sm border-zinc-400 ${form.meter_broken ? "bg-zinc-100 text-zinc-400" : ""}`}
              placeholder={form.meter_broken ? "clock u/s" : selMeter === "km" ? "km" : "hours"}
              data-testid="prestart-hours-input" />
            {form.vehicle_id && (
              <button type="button"
                onClick={() => setForm((f) => ({ ...f, meter_broken: !f.meter_broken }))}
                className={`mt-1.5 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border transition-colors ${form.meter_broken ? "bg-red-50 text-red-700 border-red-300" : "text-zinc-500 border-zinc-300 hover:border-[#FF5F15] hover:text-[#FF5F15]"}`}
                data-testid="prestart-meter-broken-toggle">
                <AlertTriangle className="w-3 h-3" />
                {form.meter_broken
                  ? "Marked not working — undo"
                  : selMeter === "km" ? "Odometer not working" : "Hour clock not working"}
              </button>
            )}
          </div>
          )}
        </div>

        {/* Machine photo (optional) */}
        <div>
          <Label className="label-tag">Machine photo <span className="text-zinc-400 normal-case">— optional</span></Label>
          {form.machine_photo_path ? (
            <div className="mt-2 relative inline-block border border-zinc-300 bg-zinc-50">
              <AuthImage path={form.machine_photo_path} alt="machine" className="h-32 w-auto object-cover" />
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, machine_photo_path: "" }))}
                className="absolute top-1 right-1 bg-zinc-900/80 text-white rounded-full p-1 hover:bg-red-600"
                data-testid="prestart-machine-photo-remove"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <label className="mt-2 flex items-center justify-center gap-2 h-20 border-2 border-dashed border-zinc-300 hover:border-[#FF5F15] cursor-pointer text-sm text-zinc-500 hover:text-[#FF5F15] transition-colors" data-testid="prestart-machine-photo-upload">
              {photoBusyKey === "__machine__" ? <span className="font-mono">Uploading…</span> : <><Camera className="w-4 h-4" /> Tap to add machine photo</>}
              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onMachinePhoto} disabled={photoBusyKey === "__machine__"} />
            </label>
          )}
        </div>

        {/* Quick-pass shortcut */}
        <div className="flex items-center justify-between border border-zinc-200 bg-zinc-50 px-3 py-2">
          <div className="text-xs uppercase tracking-wider font-bold text-zinc-600">
            {failsCount > 0 ? `${failsCount} fail(s) flagged` : "All passes by default"}
          </div>
          <Button type="button" variant="outline" size="sm"
            className="rounded-sm uppercase tracking-wider font-bold text-xs"
            onClick={() => setForm((f) => ({
              ...f, items: f.items.map((i) => ({ ...i, status: "pass", notes: "", photo_paths: [] })),
            }))}
            data-testid="prestart-reset-all-pass">
            Reset all to pass
          </Button>
        </div>

        {/* Checklist */}
        <div className="space-y-1.5">
          {form.items.map((it, idx) => (
            <ChecklistRow
              key={it.key}
              item={it}
              meta={(checklistItems || CHECKLIST)[idx]}
              busy={photoBusyKey === idx}
              onChange={(patch) => setItem(idx, patch)}
              onPhoto={(e) => onItemPhoto(idx, e)}
              testIdPrefix={`item-${it.key}`}
            />
          ))}
        </div>

        {/* Overall safe + notes */}
        <div className="border-2 border-zinc-300 p-3 space-y-3">
          <div>
            <Label className="label-tag">Safe to operate?</Label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setForm((f) => ({ ...f, overall_safe: true }))}
                className={`px-3 py-3 text-sm font-bold uppercase tracking-wider border-2 rounded-sm transition-colors ${
                  form.overall_safe ? "bg-green-600 text-white border-green-600" : "bg-white text-zinc-700 border-zinc-300 hover:border-green-500"
                }`}
                data-testid="overall-safe-yes">
                <ShieldCheck className="w-4 h-4 inline mr-1.5" /> Yes – safe
              </button>
              <button type="button" onClick={() => setForm((f) => ({ ...f, overall_safe: false }))}
                className={`px-3 py-3 text-sm font-bold uppercase tracking-wider border-2 rounded-sm transition-colors ${
                  !form.overall_safe ? "bg-red-600 text-white border-red-600" : "bg-white text-zinc-700 border-zinc-300 hover:border-red-500"
                }`}
                data-testid="overall-safe-no">
                <AlertTriangle className="w-4 h-4 inline mr-1.5" /> No – unsafe
              </button>
            </div>
            {!form.overall_safe && (
              <div className="mt-2 text-xs text-red-700 bg-red-50 border-l-2 border-red-500 p-2">
                Marking unsafe will flag this vehicle as <strong>Maintenance</strong> and notify the office.
              </div>
            )}
          </div>
          <div>
            <Label className="label-tag">Operator notes / defects</Label>
            <Textarea rows={3} value={form.defects_notes}
              onChange={(e) => setForm((f) => ({ ...f, defects_notes: e.target.value }))}
              className="mt-2 rounded-sm border-zinc-400"
              placeholder="Anything else the office should know?"
              data-testid="prestart-defects-input" />
          </div>
        </div>

        {/* Signature */}
        <div id="prestart-signature-block" className="border border-zinc-300 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <Label className="label-tag">Operator signature <span className="text-red-600">*</span></Label>
            {form.signature_path && !sigOpen && (
              <Button type="button" variant="outline" size="sm"
                className="rounded-sm uppercase tracking-wider font-bold text-xs"
                onClick={() => setSigOpen(true)} data-testid="prestart-resign">
                <PenLine className="w-3 h-3 mr-1.5" /> Re-sign
              </Button>
            )}
          </div>
          {form.signature_path && !sigOpen ? (
            <div className="border border-dashed border-zinc-300 p-2 bg-white">
              <AuthImage path={form.signature_path} alt="Signature" className="h-16 object-contain" />
            </div>
          ) : sigOpen ? (
            <SignaturePad
              testIdPrefix="prestart-sig"
              onCancel={() => setSigOpen(false)}
              onSave={onSignatureSave}
            />
          ) : (
            <Button type="button" onClick={() => setSigOpen(true)}
              className="w-full rounded-sm bg-zinc-900 text-white uppercase tracking-wider font-bold"
              data-testid="prestart-open-sign">
              <PenLine className="w-4 h-4 mr-2" /> Sign now
            </Button>
          )}
        </div>

        <DialogFooter className="sticky bottom-0 bg-white pt-3 -mx-6 px-6 border-t">
          <Button type="submit" disabled={busy}
            className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase tracking-wider font-bold"
            data-testid="prestart-submit-button">
            {busy ? "Saving..." : "Submit pre-start"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function ChecklistRow({ item, meta, busy, onChange, onPhoto, testIdPrefix }) {
  const failed = item.status === "fail";
  return (
    <div className={`border ${failed ? "border-red-500 bg-red-50" : "border-zinc-200 bg-white"} px-3 py-2`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">
          {item.label}{meta?.mech && <span className="text-[9px] text-zinc-400 ml-1 uppercase">mech</span>}
        </span>
        <div className="flex gap-1">
          {[
            { v: "pass", label: "Pass", on: "bg-green-600 text-white border-green-600", off: "text-green-700 border-green-300" },
            { v: "fail", label: "Fail", on: "bg-red-600 text-white border-red-600", off: "text-red-700 border-red-300" },
            { v: "na", label: "N/A", on: "bg-zinc-700 text-white border-zinc-700", off: "text-zinc-600 border-zinc-300" },
          ].map((b) => (
            <button
              type="button"
              key={b.v}
              onClick={() => onChange({ status: b.v, ...(b.v !== "fail" ? { notes: "", photo_paths: [] } : {}) })}
              className={`px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider border rounded-sm transition-colors ${
                item.status === b.v ? b.on : `bg-white ${b.off} hover:bg-zinc-50`
              }`}
              data-testid={`${testIdPrefix}-${b.v}`}
            >
              {b.label}
            </button>
          ))}
        </div>
      </div>
      {failed && (
        <div className="mt-2 space-y-2">
          <Textarea rows={2} value={item.notes || ""} onChange={(e) => onChange({ notes: e.target.value })}
            placeholder="What's wrong with this item?"
            className="rounded-sm border-red-300 bg-white text-sm"
            data-testid={`${testIdPrefix}-notes`} />
          <div className="flex items-center gap-2">
            <label className="inline-flex items-center gap-1.5 cursor-pointer text-xs font-bold uppercase tracking-wider text-zinc-700 hover:text-[#FF5F15]" data-testid={`${testIdPrefix}-photo-upload`}>
              <Camera className="w-3.5 h-3.5" /> {busy ? "Uploading..." : "Add photo"}
              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhoto} disabled={busy} />
            </label>
            {item.photo_paths?.length > 0 && (
              <span className="text-[10px] font-mono text-zinc-500">{item.photo_paths.length} photo(s)</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
